import { Component, Inject, ViewEncapsulation, OnDestroy, inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogActions, MatDialogContent } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { CommonModule } from '@angular/common';

import { FormResponseService } from '@services/form-response-service';

interface ReportFile { name: string; path: string }

@Component({
  selector: 'app-report-files-dialog',
  templateUrl: './modal-report-files.component.html',
  styleUrls: ['./modal-report-files.component.scss'],
  encapsulation: ViewEncapsulation.None
  , standalone: true,
  imports: [
    MatIcon,
    CommonModule,
    MatProgressSpinner,
    MatDialogActions,
    MatDialogContent,
    MatTableModule
  ]
})
export class ReportFilesDialogComponent implements OnDestroy {
  public readonly data: { stepId: string; files: ReportFile[] } = inject(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(MatDialogRef<ReportFilesDialogComponent>);
  private readonly reportService = inject(FormResponseService);

  displayedColumns = ['name', 'type', 'actions'];
  downloading: Record<string, boolean> = {};
  previewing: Record<string, boolean> = {};
  private blobUrls: string[] = [];

  onPreview(file: ReportFile) {
    if (this.previewing[file.path]) return;
    this.previewing[file.path] = true;
    const certLink =
      `${window.location.origin}/qr-code-public-panel/${this.data.stepId}` +
      `?filePath=${encodeURIComponent(file.path)}`;
    this.reportService.getSignedReport(this.data.stepId, file.path, certLink).subscribe({
      next: (blob: Blob) => {
        const url = URL.createObjectURL(blob);
        this.blobUrls.push(url);
        const newWindow = window.open(url, '_blank');

        setTimeout(() => {
          if (newWindow && !newWindow.closed) {
            const checkInterval = setInterval(() => {
              if (newWindow.closed) {
                this.revokeUrl(url);
                clearInterval(checkInterval);
              }
            }, 1000);

            setTimeout(() => {
              clearInterval(checkInterval);
              this.revokeUrl(url);
            }, 5 * 60 * 1000);
          } else {
            this.revokeUrl(url);
          }
        }, 1000);

        this.previewing[file.path] = false;
      },
      error: () => (this.previewing[file.path] = false)
    });
  }

  private revokeUrl(url: string) {
    try {
      URL.revokeObjectURL(url);
      const index = this.blobUrls.indexOf(url);
      if (index > -1) {
        this.blobUrls.splice(index, 1);
      }
    } catch { }
  }

  ngOnDestroy() {
    this.blobUrls.forEach(url => {
      try {
        URL.revokeObjectURL(url);
      } catch { }
    });
    this.blobUrls = [];
  }

  onDownload(file: ReportFile) {
    if (this.downloading[file.path]) return;
    this.downloading[file.path] = true;

    this.reportService.getSignedReport(this.data.stepId, file.path).subscribe({
      next: (blob: Blob) => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${this.sanitizeFilename(file.name || 'relatorio')}_${this.timestamp()}.pdf`;
        a.click();
        URL.revokeObjectURL(a.href);
        this.downloading[file.path] = false;
      },
      error: () => (this.downloading[file.path] = false)
    });
  }

  close() { this.dialogRef.close(); }

  private timestamp() {
    const n = new Date(), p = (x: number) => ('0' + x).slice(-2);
    return `${n.getFullYear()}${p(n.getMonth() + 1)}${p(n.getDate())}_${p(n.getHours())}${p(n.getMinutes())}${p(n.getSeconds())}`;
  }
  private sanitizeFilename(s: string) { return s.replace(/[\\/:*?'<>|]+/g, '_').trim(); }
}
